// Admin participation slice. Loads the full county list + per-status counts and
// handles per-county status writes with optimistic update + rollback. Mirrors
// the admin-blacklist slice's auth-error handling. One in-flight write is
// tracked per county id so a row can show a saving state without blocking other
// rows.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminFetch, AdminAuthError } from '../api/admin-client.js';
import { clearAdminSession } from './admin-auth-slice.js';

const handleThunkError = (err, dispatch, rejectWithValue) => {
  if (err instanceof AdminAuthError) dispatch(clearAdminSession());
  return rejectWithValue(err.message);
};

export const fetchParticipation = createAsyncThunk(
  'adminParticipation/fetch',
  async (_arg, { dispatch, rejectWithValue }) => {
    try {
      return await adminFetch('/api/admin/participation');
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

export const setCountyStatus = createAsyncThunk(
  'adminParticipation/setStatus',
  async ({ countyId, status }, { dispatch, rejectWithValue }) => {
    try {
      const { item } = await adminFetch(`/api/admin/participation/${countyId}`, {
        method: 'PUT',
        body: { status },
      });
      return { item };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

const recountStatuses = (items) => items.reduce((acc, it) => {
  acc[it.status] = (acc[it.status] ?? 0) + 1;
  return acc;
}, {});

const initialState = {
  items: [],
  counts: {},
  loading: false,
  error: null,
  // county ids with an in-flight status write
  saving: [],
  saveError: null,
};

const slice = createSlice({
  name: 'adminParticipation',
  initialState,
  reducers: {
    clearSaveError(state) {
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchParticipation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchParticipation.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.counts = action.payload.counts;
      })
      .addCase(fetchParticipation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load participation.';
      })
      .addCase(setCountyStatus.pending, (state, action) => {
        const { countyId } = action.meta.arg;
        if (!state.saving.includes(countyId)) state.saving.push(countyId);
        state.saveError = null;
      })
      .addCase(setCountyStatus.fulfilled, (state, action) => {
        const { item } = action.payload;
        state.saving = state.saving.filter((id) => id !== item.countyId);
        const row = state.items.find((it) => it.countyId === item.countyId);
        if (row) {
          row.status = item.status;
          row.updatedAt = item.updatedAt;
        }
        state.counts = recountStatuses(state.items);
      })
      .addCase(setCountyStatus.rejected, (state, action) => {
        const { countyId } = action.meta.arg;
        state.saving = state.saving.filter((id) => id !== countyId);
        state.saveError = action.payload ?? 'Failed to save status.';
      });
  },
});

export const { clearSaveError } = slice.actions;
export default slice.reducer;
